import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import PageHeader from "../../components/common/PageHeader";
import { useXhr } from "hooks/useXhr";
import { usePrinciplesContext } from "hooks/usePrinciplesContext";
import ReactQuill from 'react-quill';
import Quill from 'quill';
import 'react-quill/dist/quill.snow.css';
import { Button, Spinner } from "react-bootstrap";

function Principles() {
    //States
    const [status, setStatus] = useState(false);
    const [isUpdate, setIsUpdate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [lang, setLang] = useState(null);
    const [error, setError] = useState({
        field: null,
        note: null
    });

    //Hooks
    const { customXhr } = useXhr()
    const { principles, dispatch } = usePrinciplesContext();

    //Quill Config
    const SizeStyle = Quill.import("attributors/style/size");
    SizeStyle.whitelist = ['12px', '14px', '16px', '18px', '24px', '32px', '48px'];
    Quill.register(SizeStyle, true);

    const AlignStyle = Quill.import("attributors/style/align");
    Quill.register(AlignStyle, true);

    const modules = {
        toolbar: [
            [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
            [{ 'size': ['12px', '14px', '16px', '18px', '24px', '32px', '48px'] }],
            [{ 'align': [] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' },
            { 'indent': '-1' }, { 'indent': '+1' }],
            ['link'],
            ['clean'],
            [{ 'color': [] }, { 'background': [] }]
        ]
    };

    //Constants
    const requiredFields = ['alt1', 'alt2', 'alt3', 'alt4', 'vision', 'mission', 'goals', 'principles'];
    const maxTextLength = 500;

    //Functions
    const handleOnChange = (e) => {
        const { id, value } = e.target;

        setIsUpdate(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    const handleTextInput = (e, field) => {
        setIsUpdate(prevState => ({
            ...prevState,
            [field]: e
        }));
    };

    const generateUniqueFileName = (file) => {
        const timestamp = Date.now();
        const randomNumber = Math.floor(Math.random() * 10000);
        const extension = file.name.split('.').pop();

        return `${timestamp}_${randomNumber}.${extension}`;
    };

    const handlePhotoInput = (e) => {
        const file = e.target.files[0];
        const id = e.target.id;

        const newFileName = generateUniqueFileName(file);
        const renamedFile = new File([file], newFileName, { type: file.type });

        setIsUpdate(prevState => ({
            ...prevState,
            [id]: renamedFile
        }));
    };

    const handleSubmit = async () => {
        for (const field of requiredFields) {
            if (!isUpdate[field] || isUpdate[field] === "") {
                return (
                    setError({
                        field: field,
                        note: `Zorunlu alan`
                    }),
                    setIsLoading(false)
                )
            }
        }
        setIsLoading(true);
        const formData = new FormData();
        formData.append('module', 'principles');
        formData.append('action', 'update');
        formData.append('id', isUpdate.id);
        formData.append('firstPhoto', isUpdate.firstPhoto);
        formData.append('secondPhoto', isUpdate.secondPhoto);
        formData.append('thirdPhoto', isUpdate.thirdPhoto);
        formData.append('fourthPhoto', isUpdate.fourthPhoto);
        formData.append('visionPhoto', isUpdate.visionPhoto);
        formData.append('visionEngPhoto', isUpdate.visionEngPhoto);
        formData.append('visionFraPhoto', isUpdate.visionFraPhoto);
        formData.append('visionGerPhoto', isUpdate.visionGerPhoto);
        formData.append('visionDutPhoto', isUpdate.visionDutPhoto);
        formData.append('missionPhoto', isUpdate.missionPhoto);
        formData.append('missionEngPhoto', isUpdate.missionEngPhoto);
        formData.append('missionFraPhoto', isUpdate.missionFraPhoto);
        formData.append('missionGerPhoto', isUpdate.missionGerPhoto);
        formData.append('missionDutPhoto', isUpdate.missionDutPhoto);
        formData.append('alt1', isUpdate.alt1);
        formData.append('alt2', isUpdate.alt2);
        formData.append('alt3', isUpdate.alt3);
        formData.append('alt4', isUpdate.alt4);
        formData.append('vision', isUpdate.vision);
        formData.append('visionEng', isUpdate.visionEng);
        formData.append('visionFra', isUpdate.visionFra);
        formData.append('visionGer', isUpdate.visionGer);
        formData.append('visionDut', isUpdate.visionDut);
        formData.append('mission', isUpdate.mission);
        formData.append('missionEng', isUpdate.missionEng);
        formData.append('missionFra', isUpdate.missionFra);
        formData.append('missionGer', isUpdate.missionGer);
        formData.append('missionDut', isUpdate.missionDut);
        formData.append('goals', isUpdate.goals);
        formData.append('goalsEng', isUpdate.goalsEng);
        formData.append('goalsFra', isUpdate.goalsFra);
        formData.append('goalsGer', isUpdate.goalsGer);
        formData.append('goalsDut', isUpdate.goalsDut);
        formData.append('principles', isUpdate.principles);
        formData.append('principlesEng', isUpdate.principlesEng);
        formData.append('principlesFra', isUpdate.principlesFra);
        formData.append('principlesGer', isUpdate.principlesGer);
        formData.append('principlesDut', isUpdate.principlesDut);

        const response = await customXhr(formData);

        if (response.status === "success") {
            setIsUpdate(null);
            setLang(null);
            setStatus(!status);
            setError({
                field: null,
                note: null
            });
            setIsLoading(false);
        };
    };

    const getPrinciples = async () => {
        const formData = new FormData();
        formData.append('module', 'principles');
        formData.append('action', 'list');

        const response = await customXhr(formData);

        if (response) {
            dispatch({ type: 'SET_PRINCIPLES', payload: response[0] });
        };
    };

    const lengthError = (field) => {
        if (isUpdate && isUpdate[field] && maxTextLength - isUpdate[field].length < 20) {
            return <span className="text-danger">
                {`${maxTextLength - (isUpdate && isUpdate[field] ? isUpdate[field].length : 0)} harf hakkınız kaldı!`}
            </span>;
        } else {
            return "";
        }
    };

    useEffect(() => {
        getPrinciples();
    }, [status]);

    return (
        <div className="container-xxl">
            <PageHeader headerTitle="Prensipler" renderRight={() => {
                return <div className="btn-group" role="group" aria-label="Basic outlined example">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        title="Türkçe Güncelleme"
                        onClick={() => { setIsUpdate(principles); setLang("tur"); }}
                    >
                        <i className="icofont-edit text-success"></i>
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        title="İngilizce Güncelleme"
                        onClick={() => { setIsUpdate(principles); setLang("eng"); }}
                    >
                        <img src={process.env.PUBLIC_URL + `/flags/united-kingdom.png`} style={{ width: "15px" }} />
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        title="Fransızca Güncelleme"
                        onClick={() => { setIsUpdate(principles); setLang("fra"); }}
                    >
                        <img src={process.env.PUBLIC_URL + `/flags/france.png`} style={{ width: "15px" }} />
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        title="Almanca Güncelleme"
                        onClick={() => { setIsUpdate(principles); setLang("ger"); }}
                    >
                        <img src={process.env.PUBLIC_URL + `/flags/germany.png`} style={{ width: "15px" }} />
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        title="Felemenkçe Güncelleme"
                        onClick={() => { setIsUpdate(principles); setLang("dut"); }}
                    >
                        <img src={process.env.PUBLIC_URL + `/flags/netherlands.png`} style={{ width: "15px" }} />
                    </button>
                </div>
            }} />
            <div className="row align-items-center justify-content-center">
                <div className="col-lg-12 col-md-12 flex-column" style={{width: "80%"}}>
                    {principles &&
                        <div className="row g-3 gy-5 py-3 row-deck d-flex flex-wrap">
                            <div className='col-12 col-lg-6 p-0 order-1 order-lg-1'>
                                <img src={process.env.PUBLIC_URL + `/uploads/principles/${principles.firstPhotoName}`} className="w-100" />
                            </div>
                            <div className='col-12 col-lg-6 p-0 order-2 order-lg-2'>
                                <img src={process.env.PUBLIC_URL + `/uploads/principles/${principles.visionPhotoName}`} className="w-100" />
                            </div>
                            <div className='col-12 col-lg-6 p-0 m-0 order-3 order-lg-3'>
                                <img src={process.env.PUBLIC_URL + `/uploads/principles/${principles.missionPhotoName}`} className="w-100" />
                            </div>
                            <div className='col-12 col-lg-6 p-0 m-0 order-2 order-lg-4'>
                                <img src={process.env.PUBLIC_URL + `/uploads/principles/${principles.secondPhotoName}`} className='w-100' />
                            </div>
                            <div className='second-section col-12 col-lg-12 order-3 order-lg-5 w-100 p-0 mt-0 mb-5 d-block'>
                                <div className='message-second-photo'>
                                    <img src={process.env.PUBLIC_URL + `/uploads/principles/${principles.thirdPhotoName}`} className='mb-5 w-100' />
                                </div>
                                <p className='mb-50 fw-bold' style={{ fontSize: "40px" }}>{"İlkeler"}</p>
                                <div className="dangerousText" dangerouslySetInnerHTML={{ __html: principles.principles }} />
                            </div>
                            <div className='col-12 col-lg-6 ps-0 pe-5 order-4 order-lg-5 mb-5'>
                                <img src={process.env.PUBLIC_URL + `/uploads/principles/${principles.fourthPhotoName}`} className='w-100' />
                            </div>
                            <div className='col-12 col-lg-6 ps-5 pe-0 order-5 order-lg-5 mb-5 d-block'>
                                <p className='mb-50 fw-bold' style={{ fontSize: "40px" }}>{"Hedefler"}</p>
                                <div className="dangerousText" dangerouslySetInnerHTML={{ __html: principles.goals }} />
                            </div>
                        </div>
                    }
                </div>
            </div>
            <Modal
                show={lang === "tur"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
                dialogClassName="modal-xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Prensipleri Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="vision" className="form-label">
                            {"Vizyon "}
                            {lengthError("vision")}
                        </label>
                        <textarea
                            className="form-control"
                            id="vision"
                            rows="5"
                            value={isUpdate && isUpdate.vision}
                            onChange={handleOnChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "vision" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "vision" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="mission" className="form-label">
                            {"Misyon "}
                            {lengthError("mission")}
                        </label>
                        <textarea
                            className="form-control"
                            id="mission"
                            rows="5"
                            value={isUpdate && isUpdate.mission}
                            onChange={handleOnChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "mission" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "mission" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="goals" className="form-label">
                            {"Hedefler"}
                        </label>
                        {isUpdate &&
                            <ReactQuill
                                theme="snow"
                                id="goals"
                                value={isUpdate.goals}
                                onChange={(e) => handleTextInput(e, "goals")}
                                style={{ borderColor: `${error.field === "goals" ? "red" : "var(--border-color)"}` }}
                                modules={modules}
                            />}
                        {error.field === "goals" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                    <div className="mb-3">
                        <label htmlFor="principles" className="form-label">
                            {"İlkeler"}
                        </label>
                        {isUpdate &&
                            <ReactQuill
                                theme="snow"
                                id="principles"
                                value={isUpdate.principles}
                                onChange={(e) => handleTextInput(e, "principles")}
                                style={{ borderColor: `${error.field === "principles" ? "red" : "var(--border-color)"}` }}
                                modules={modules}
                            />}
                        {error.field === "principles" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col-6">
                                <label htmlFor="alt1" className="form-label">
                                    {"Seo Kelimeler "}
                                    <span className="text-danger">
                                        (Lütfen 5 Kelimeden Fazla Yazmayınız!)
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="alt1"
                                    value={isUpdate && isUpdate.alt1}
                                    onChange={handleOnChange}
                                    style={{ borderColor: `${error.field === "alt1" ? "red" : "var(--border-color)"}` }}
                                />
                                {error.field === "alt2" &&
                                    <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                                }
                            </div>
                            <div className="col-6">
                                <label htmlFor="alt2" className="form-label">
                                    {"Seo Kelimeler "}
                                    <span className="text-danger">
                                        (Lütfen 5 Kelimeden Fazla Yazmayınız!)
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="alt2"
                                    value={isUpdate && isUpdate.alt2}
                                    onChange={handleOnChange}
                                    style={{ borderColor: `${error.field === "alt2" ? "red" : "var(--border-color)"}` }}
                                />
                                {error.field === "alt2" &&
                                    <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                                }
                            </div>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col-6">
                                <label htmlFor="alt3" className="form-label">
                                    {"Seo Kelimeler "}
                                    <span className="text-danger">
                                        (Lütfen 5 Kelimeden Fazla Yazmayınız!)
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="alt3"
                                    value={isUpdate && isUpdate.alt3}
                                    onChange={handleOnChange}
                                    style={{ borderColor: `${error.field === "alt3" ? "red" : "var(--border-color)"}` }}
                                />
                                {error.field === "alt3" &&
                                    <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                                }
                            </div>
                            <div className="col-6">
                                <label htmlFor="alt4" className="form-label">
                                    {"Seo Kelimeler "}
                                    <span className="text-danger">
                                        (Lütfen 5 Kelimeden Fazla Yazmayınız!)
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="alt4"
                                    value={isUpdate && isUpdate.alt4}
                                    onChange={handleOnChange}
                                    style={{ borderColor: `${error.field === "alt4" ? "red" : "var(--border-color)"}` }}
                                />
                                {error.field === "alt4" &&
                                    <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                                }
                            </div>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col-6">
                                <label htmlFor="firstPhoto" className="form-label">
                                    {"1.Fotoğraf Yükle"}
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="firstPhoto"
                                    onChange={handlePhotoInput}
                                />
                                <div className="form-label" style={{ color: "red" }}>
                                    {"Bu alanın geçerli görsel öğe boyutu: 1424x1424"}
                                </div>
                            </div>
                            <div className="col-6">
                                <label htmlFor="secondPhoto" className="form-label">
                                    {"2.Fotoğraf Yükle"}
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="secondPhoto"
                                    onChange={handlePhotoInput}
                                />
                                <div className="form-label" style={{ color: "red" }}>
                                    {"Bu alanın geçerli görsel öğe boyutu: 1424x1424"}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col-6">
                                <label htmlFor="thirdPhoto" className="form-label">
                                    {"3.Fotoğraf Yükle"}
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="thirdPhoto"
                                    onChange={handlePhotoInput}
                                />
                                <div className="form-label" style={{ color: "red" }}>
                                    {"Bu alanın geçerli görsel öğe boyutu: 2848x1078"}
                                </div>
                            </div>
                            <div className="col-6">
                                <label htmlFor="fourthPhoto" className="form-label">
                                    {"4.Fotoğraf Yükle"}
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="fourthPhoto"
                                    onChange={handlePhotoInput}
                                />
                                <div className="form-label" style={{ color: "red" }}>
                                    {"Bu alanın geçerli görsel öğe boyutu: 1332x1829"}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col-6">
                                <label htmlFor="visionPhoto" className="form-label">
                                    {"Vizyon Fotoğraf Yükle"}
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="visionPhoto"
                                    onChange={handlePhotoInput}
                                />
                                <div className="form-label" style={{ color: "red" }}>
                                    {"Bu alanın geçerli görsel öğe boyutu: 1424x1424"}
                                </div>
                            </div>
                            <div className="col-6">
                                <label htmlFor="missionPhoto" className="form-label">
                                    {"Misyon Fotoğraf Yükle"}
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="missionPhoto"
                                    onChange={handlePhotoInput}
                                />
                                <div className="form-label" style={{ color: "red" }}>
                                    {"Bu alanın geçerli görsel öğe boyutu: 1424x1424"}
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleSubmit()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "eng"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
                dialogClassName="modal-xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">İngilizce Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="visionEng" className="form-label">
                            {"Vizyon "}
                            {lengthError("visionEng")}
                        </label>
                        <textarea
                            className="form-control"
                            id="visionEng"
                            rows="5"
                            value={isUpdate && isUpdate.visionEng}
                            onChange={handleOnChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "visionEng" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "visionEng" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="missionEng" className="form-label">
                            {"Misyon "}
                            {lengthError("missionEng")}
                        </label>
                        <textarea
                            className="form-control"
                            id="missionEng"
                            rows="5"
                            value={isUpdate && isUpdate.missionEng}
                            onChange={handleOnChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "missionEng" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "missionEng" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="goalsEng" className="form-label">
                            {"Hedefler"}
                        </label>
                        {isUpdate &&
                            <ReactQuill
                                theme="snow"
                                id="goalsEng"
                                value={isUpdate.goalsEng}
                                onChange={(e) => handleTextInput(e, "goalsEng")}
                                style={{ borderColor: `${error.field === "goalsEng" ? "red" : "var(--border-color)"}` }}
                                modules={modules}
                            />}
                        {error.field === "goalsEng" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                    <div className="mb-3">
                        <label htmlFor="principlesEng" className="form-label">
                            {"İlkeler"}
                        </label>
                        {isUpdate &&
                            <ReactQuill
                                theme="snow"
                                id="principlesEng"
                                value={isUpdate.principlesEng}
                                onChange={(e) => handleTextInput(e, "principlesEng")}
                                style={{ borderColor: `${error.field === "principlesEng" ? "red" : "var(--border-color)"}` }}
                                modules={modules}
                            />}
                        {error.field === "principlesEng" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col-6">
                                <label htmlFor="visionEngPhoto" className="form-label">
                                    {"Vizyon Fotoğraf Yükle"}
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="visionEngPhoto"
                                    onChange={handlePhotoInput}
                                />
                                <div className="form-label" style={{ color: "red" }}>
                                    {"Bu alanın geçerli görsel öğe boyutu: 1424x1424"}
                                </div>
                            </div>
                            <div className="col-6">
                                <label htmlFor="missionEngPhoto" className="form-label">
                                    {"Misyon Fotoğraf Yükle"}
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="missionEngPhoto"
                                    onChange={handlePhotoInput}
                                />
                                <div className="form-label" style={{ color: "red" }}>
                                    {"Bu alanın geçerli görsel öğe boyutu: 1424x1424"}
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleSubmit()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "fra"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
                dialogClassName="modal-xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Fransızca Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="visionFra" className="form-label">
                            {"Vizyon "}
                            {lengthError("visionFra")}
                        </label>
                        <textarea
                            className="form-control"
                            id="visionFra"
                            rows="5"
                            value={isUpdate && isUpdate.visionFra}
                            onChange={handleOnChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "visionFra" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "visionFra" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="missionFra" className="form-label">
                            {"Misyon "}
                            {lengthError("missionFra")}
                        </label>
                        <textarea
                            className="form-control"
                            id="missionFra"
                            rows="5"
                            value={isUpdate && isUpdate.missionFra}
                            onChange={handleOnChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "missionFra" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "missionFra" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="goalsFra" className="form-label">
                            {"Hedefler"}
                        </label>
                        {isUpdate &&
                            <ReactQuill
                                theme="snow"
                                id="goalsFra"
                                value={isUpdate.goalsFra}
                                onChange={(e) => handleTextInput(e, "goalsFra")}
                                style={{ borderColor: `${error.field === "goalsFra" ? "red" : "var(--border-color)"}` }}
                                modules={modules}
                            />}
                        {error.field === "goalsFra" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                    <div className="mb-3">
                        <label htmlFor="principlesFra" className="form-label">
                            {"İlkeler"}
                        </label>
                        {isUpdate &&
                            <ReactQuill
                                theme="snow"
                                id="principlesFra"
                                value={isUpdate.principlesFra}
                                onChange={(e) => handleTextInput(e, "principlesFra")}
                                style={{ borderColor: `${error.field === "principlesFra" ? "red" : "var(--border-color)"}` }}
                                modules={modules}
                            />}
                        {error.field === "principlesFra" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col-6">
                                <label htmlFor="visionFraPhoto" className="form-label">
                                    {"Vizyon Fotoğraf Yükle"}
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="visionFraPhoto"
                                    onChange={handlePhotoInput}
                                />
                                <div className="form-label" style={{ color: "red" }}>
                                    {"Bu alanın geçerli görsel öğe boyutu: 1424x1424"}
                                </div>
                            </div>
                            <div className="col-6">
                                <label htmlFor="missionFraPhoto" className="form-label">
                                    {"Misyon Fotoğraf Yükle"}
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="missionFraPhoto"
                                    onChange={handlePhotoInput}
                                />
                                <div className="form-label" style={{ color: "red" }}>
                                    {"Bu alanın geçerli görsel öğe boyutu: 1424x1424"}
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleSubmit()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "ger"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
                dialogClassName="modal-xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Almanca Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="visionGer" className="form-label">
                            {"Vizyon "}
                            {lengthError("visionGer")}
                        </label>
                        <textarea
                            className="form-control"
                            id="visionGer"
                            rows="5"
                            value={isUpdate && isUpdate.visionGer}
                            onChange={handleOnChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "visionGer" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "visionGer" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="missionGer" className="form-label">
                            {"Misyon "}
                            {lengthError("missionGer")}
                        </label>
                        <textarea
                            className="form-control"
                            id="missionGer"
                            rows="5"
                            value={isUpdate && isUpdate.missionGer}
                            onChange={handleOnChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "missionGer" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "missionGer" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="goalsGer" className="form-label">
                            {"Hedefler"}
                        </label>
                        {isUpdate &&
                            <ReactQuill
                                theme="snow"
                                id="goalsGer"
                                value={isUpdate.goalsGer}
                                onChange={(e) => handleTextInput(e, "goalsGer")}
                                style={{ borderColor: `${error.field === "goalsGer" ? "red" : "var(--border-color)"}` }}
                                modules={modules}
                            />}
                        {error.field === "goalsGer" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                    <div className="mb-3">
                        <label htmlFor="principlesGer" className="form-label">
                            {"İlkeler"}
                        </label>
                        {isUpdate &&
                            <ReactQuill
                                theme="snow"
                                id="principlesGer"
                                value={isUpdate.principlesGer}
                                onChange={(e) => handleTextInput(e, "principlesGer")}
                                style={{ borderColor: `${error.field === "principlesGer" ? "red" : "var(--border-color)"}` }}
                                modules={modules}
                            />}
                        {error.field === "principlesGer" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col-6">
                                <label htmlFor="visionGerPhoto" className="form-label">
                                    {"Vizyon Fotoğraf Yükle"}
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="visionGerPhoto"
                                    onChange={handlePhotoInput}
                                />
                                <div className="form-label" style={{ color: "red" }}>
                                    {"Bu alanın geçerli görsel öğe boyutu: 1424x1424"}
                                </div>
                            </div>
                            <div className="col-6">
                                <label htmlFor="missionGerPhoto" className="form-label">
                                    {"Misyon Fotoğraf Yükle"}
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="missionGerPhoto"
                                    onChange={handlePhotoInput}
                                />
                                <div className="form-label" style={{ color: "red" }}>
                                    {"Bu alanın geçerli görsel öğe boyutu: 1424x1424"}
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleSubmit()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "dut"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
                dialogClassName="modal-xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Felemenkçe Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="visionDut" className="form-label">
                            {"Vizyon "}
                            {lengthError("visionDut")}
                        </label>
                        <textarea
                            className="form-control"
                            id="visionDut"
                            rows="5"
                            value={isUpdate && isUpdate.visionDut}
                            onChange={handleOnChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "visionDut" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "visionDut" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="missionDut" className="form-label">
                            {"Misyon "}
                            {lengthError("missionDut")}
                        </label>
                        <textarea
                            className="form-control"
                            id="missionDut"
                            rows="5"
                            value={isUpdate && isUpdate.missionDut}
                            onChange={handleOnChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "missionDut" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "missionDut" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="goalsDut" className="form-label">
                            {"Hedefler"}
                        </label>
                        {isUpdate &&
                            <ReactQuill
                                theme="snow"
                                id="goalsDut"
                                value={isUpdate.goalsDut}
                                onChange={(e) => handleTextInput(e, "goalsDut")}
                                style={{ borderColor: `${error.field === "goalsDut" ? "red" : "var(--border-color)"}` }}
                                modules={modules}
                            />}
                        {error.field === "goalsDut" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                    <div className="mb-3">
                        <label htmlFor="principlesDut" className="form-label">
                            {"İlkeler"}
                        </label>
                        {isUpdate &&
                            <ReactQuill
                                theme="snow"
                                id="principlesDut"
                                value={isUpdate.principlesDut}
                                onChange={(e) => handleTextInput(e, "principlesDut")}
                                style={{ borderColor: `${error.field === "principlesDut" ? "red" : "var(--border-color)"}` }}
                                modules={modules}
                            />}
                        {error.field === "principlesDut" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                    <div className="mb-3">
                        <div className="row">
                            <div className="col-6">
                                <label htmlFor="visionDutPhoto" className="form-label">
                                    {"Vizyon Fotoğraf Yükle"}
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="visionDutPhoto"
                                    onChange={handlePhotoInput}
                                />
                                <div className="form-label" style={{ color: "red" }}>
                                    {"Bu alanın geçerli görsel öğe boyutu: 1424x1424"}
                                </div>
                            </div>
                            <div className="col-6">
                                <label htmlFor="missionDutPhoto" className="form-label">
                                    {"Misyon Fotoğraf Yükle"}
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="missionDutPhoto"
                                    onChange={handlePhotoInput}
                                />
                                <div className="form-label" style={{ color: "red" }}>
                                    {"Bu alanın geçerli görsel öğe boyutu: 1424x1424"}
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleSubmit()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default Principles;